import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowDown, Clock, BarChart3, Bed, CheckCircle, Info } from "lucide-react";

const SpO2Widget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const spo2Tab = tabs.find((tab) => tab.id === "spo2");
    const widget = spo2Tab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/90 via-white/80 to-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 mb-3">
                <div className="flex justify-between items-center mb-3">
                    <h5 className="text-blue-800 font-medium flex items-center">
                        <Clock className={`w-4 h-4 ${isRTL ? "ml-1" : "mr-1"} text-blue-500`} />
                        {widget.title}
                    </h5>
                    <div className="flex items-center">
                        <span className="text-xs bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-3 py-1.5 rounded-full font-medium shadow-sm">{widget.value}</span>
                    </div>
                </div>

                {/* Circular display */}
                <div className="relative h-32 mb-4 overflow-hidden rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-cyan-400/10 to-blue-300/10"></div>

                    <div className="relative">
                        <svg width="110" height="110" viewBox="0 0 110 110">
                            <circle cx="55" cy="55" r="50" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                            <circle cx="55" cy="55" r="42" fill="none" stroke="#e5e7eb" strokeWidth="3" strokeDasharray="3,3" />
                            <circle cx="55" cy="55" r="35" fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="2,2" />

                            <motion.circle
                                cx="55"
                                cy="55"
                                r="50"
                                fill="none"
                                stroke="url(#spo2Gradient)"
                                strokeWidth="8"
                                strokeDasharray="314"
                                strokeDashoffset="6.3"
                                initial={{ strokeDashoffset: 314 }}
                                animate={{ strokeDashoffset: 6.3 }}
                                transition={{ duration: 1.8, ease: "easeInOut" }}
                            />

                            <defs>
                                <linearGradient id="spo2Gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="50%" stopColor="#06b6d4" />
                                    <stop offset="100%" stopColor="#0ea5e9" />
                                </linearGradient>
                            </defs>
                        </svg>

                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.span
                                className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500"
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
                            >
                                {widget.value}
                            </motion.span>
                            <motion.div className="flex items-center text-xs text-blue-600" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.3 }}>
                                <span>{widget.title}</span>
                            </motion.div>
                        </div>
                    </div>

                    <motion.div
                        className="absolute top-2 right-2 text-blue-400"
                        animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <ArrowDown className="w-6 h-6 text-blue-400" />
                    </motion.div>

                    <motion.div
                        className="absolute bottom-2 left-2 flex items-center text-xs text-blue-600"
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7, duration: 0.5 }}
                    >
                        <span className="bg-blue-100/70 px-2 py-0.5 rounded-full">{widget.levelLabel}</span>
                    </motion.div>
                </div>

                {/* Status */}
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600 flex items-center">
                        <Clock className={`w-3.5 h-3.5 ${isRTL ? "ml-1" : "mr-1"} mt-0.5 text-gray-600`} />
                        {widget.statusLabel}
                    </span>
                    <motion.span
                        className="text-sm font-medium text-green-600 flex items-center bg-green-50 px-2 py-0.5 rounded-full"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.3 }}
                    >
                        <CheckCircle className={`w-3.5 h-3.5 ${isRTL ? "ml-1" : "mr-1"} text-green-600`} />
                        {widget.status}
                    </motion.span>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                {/* 24h trend */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-xl p-3 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-100"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 flex items-center">
                            <BarChart3 className={`w-3 h-3 ${isRTL ? "ml-1" : "mr-1"} text-gray-600`} />
                            {widget.trend24h}
                        </span>
                    </div>

                    <div className="relative h-14">
                        <svg className="w-full h-full" viewBox="0 0 100 40">
                            <line x1="0" y1="20" x2="100" y2="20" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,2" />
                            <line x1="0" y1="10" x2="100" y2="10" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,2" />
                            <line x1="0" y1="30" x2="100" y2="30" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,2" />

                            <motion.path
                                d="M 0,20 Q 10,18 20,19 T 40,20 T 60,18 T 80,22 T 100,20 V 40 H 0 Z"
                                fill="url(#spo2ChartGradient)"
                                fillOpacity="0.2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 1 }}
                            />

                            <motion.path
                                d="M 0,20 Q 10,18 20,19 T 40,20 T 60,18 T 80,22 T 100,20"
                                fill="none"
                                stroke="url(#spo2ChartGradient)"
                                strokeWidth="2"
                                filter="url(#chartGlow)"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                transition={{ duration: 1.5, ease: "easeInOut" }}
                            />

                            <motion.circle cx="60" cy="18" r="2.5" fill="#3b82f6" filter="url(#chartGlow)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.3 }} />

                            <defs>
                                <linearGradient id="spo2ChartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#06b6d4" />
                                </linearGradient>
                                <filter id="chartGlow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feGaussianBlur stdDeviation="1" result="blur" />
                                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                </filter>
                            </defs>
                        </svg>

                        <div className="absolute bottom-0 w-full flex justify-between text-[8px] text-gray-500 bg-white/50 rounded-sm py-0.5">
                            <span>{widget.timeLabels?.morning || "Morning"}</span>
                            <span>{widget.timeLabels?.noon || "Noon"}</span>
                            <span>{widget.timeLabels?.night || "Night"}</span>
                        </div>
                    </div>
                </motion.div>

                {/* Min during sleep */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-xl p-3 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-100"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.4, ease: "easeOut" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 flex items-center">
                            <Bed className={`w-3 h-3 ${isRTL ? "ml-1" : "mr-1"} text-gray-600`} />
                            {widget.minSleep?.label}
                        </span>
                        <span className="text-xs font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-md">{widget.minSleep?.value}</span>
                    </div>

                    <div className="relative h-14 flex items-center">
                        <div className="w-full flex items-center">
                            <div className={`flex-1 me-2`}>
                                <div className="w-full bg-gradient-to-r from-gray-200 to-gray-100 rounded-full h-2.5 overflow-hidden">
                                    <motion.div
                                        className="bg-gradient-to-r from-blue-500 to-cyan-400 h-2.5 rounded-full"
                                        style={{ width: "95%" }}
                                        initial={{ width: 0 }}
                                        animate={{ width: "95%" }}
                                        transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
                                    />
                                </div>
                                <div className="flex justify-between text-[8px] text-gray-500 mt-1">
                                    <span>90%</span>
                                    <span className="text-blue-600 font-medium">95%</span>
                                    <span>100%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Tips */}
            <motion.div
                className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg shadow-sm border border-blue-100"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.4, ease: "easeOut" }}
            >
                <div className="flex items-center mb-2">
                    <Info className={`w-4 h-4 text-blue-500 ${isRTL ? "ml-1" : "mr-1"}`} />
                    <span className="text-sm font-medium text-blue-700 mb-0.5">{widget.tips?.title}</span>
                </div>
                <ul className="text-xs text-blue-700 space-y-1.5">
                    {widget.tips?.items?.map((item: string, index: number) => (
                        <motion.li key={index} className="flex items-start" initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 + index * 0.2, duration: 0.3 }}>
                            <CheckCircle className={`w-3.5 h-3.5 text-blue-500 mt-0.5 ${isRTL ? "ml-1" : "mr-1"} flex-shrink-0`} />
                            {item}
                        </motion.li>
                    ))}
                </ul>
            </motion.div>
        </motion.div>
    );
};

export default SpO2Widget;
